import React from "react";

import { CloseRounded, SaveRounded } from "@mui/icons-material";
import { Avatar, Box, DialogContent, TextField } from "@mui/material";

import useAuth from "../../config/context/authContext";
import useModal from "../../config/context/modalContext";
import { AuthService } from "../../config/services/AuthService";
import { GreyPallet } from "../../config/styles";
import { Dev } from "../../config/util/consoleUtil";
import UtilFunctions from "../../config/util/utilFunctions";

import MainSolidButton from "../MainSolidButton";
import MainText from "../MainText";
import { ModalTransition } from "../ModalTransition";
import PageHeader from "../PageHeader";

import { ModalBaseType } from "../../types/common";
import { WideDialog } from "../MainModalSimple/styles";

export default function ModalUserName({ onClose }: ModalBaseType) {
  const [open, setOpen] = React.useState(true);

  const { pushAlert } = useModal();

  const ScrollBoxRef = React.useRef(null);
  const SelectedColorRef = React.useRef(null);

  const AuthUpdate = AuthService.UpdateOrCreate();

  const { currentUserState, currentUserInfo, fetchUserInfo } = useAuth();

  const [loading, setLoading] = React.useState(false);

  const [selectedColor, setSelectedColor] = React.useState<string>(UtilFunctions.randomColor());

  const [textField, setTextField] = React.useState(currentUserInfo?.name || "");

  const handleSave = async () => {
    try {
      setLoading(true);
      let payload: { name: string; color?: string } = { name: textField, color: selectedColor };

      if (currentUserInfo?.color) {
        delete payload.color;
      }

      await AuthUpdate.runRequest(String(currentUserState?.uid), payload);

      await fetchUserInfo();

      pushAlert({
        type: "success",
        message: "Seu nome foi alterado",
      });

      handleClose();
    } catch (error) {
      console.log(...new Dev("handleSave", error).Error());

      pushAlert({
        type: "error",
        message: "Erro ao alterar nome",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 200);
  };

  const handleScrollTo = () => {
    if (SelectedColorRef.current && ScrollBoxRef.current) {
      const selectedOffset = (SelectedColorRef?.current as any)?.offsetLeft - 28;

      (ScrollBoxRef?.current as any)?.scrollTo({ left: selectedOffset, behavior: "smooth" });
    }
  };

  React.useEffect(() => {
    handleScrollTo();
  }, [SelectedColorRef.current, ScrollBoxRef.current]);

  React.useEffect(() => {
    setTimeout(() => {
      handleScrollTo();
    }, 500);
  }, []);

  return (
    <WideDialog
      fullWidth={true}
      open={open}
      onClose={handleClose}
      TransitionComponent={ModalTransition}
    >
      <DialogContent>
        <PageHeader
          size="subtitle"
          title={"Editar Nome"}
          rightIcon={<CloseRounded />}
          onRightAction={handleClose}
        />

        <Box>
          <Box mt={4}>
            <TextField
              autoCapitalize="sentences"
              fullWidth
              label="Nome"
              variant="outlined"
              sx={{
                "& .MuiFormLabel-root": {
                  fontWeight: "bold",
                  color: `${GreyPallet.Regular} !important`,
                },

                "& .MuiOutlinedInput-root": {
                  borderRadius: "15px",
                  height: 60,
                },

                "& .MuiInputBase-input": {
                  fontWeight: "bold",
                  fontSize: 16,
                  color: GreyPallet.Regular,
                },

                "& .MuiSvgIcon-root": {
                  fontSize: 26,
                },

                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth: "3px !important",
                  borderColor: `${GreyPallet.Regular} !important`,
                  borderStyle: "solid",
                  "& .Mui-focused": {
                    borderColor: `${GreyPallet.Regular} !important`,
                  },
                },
              }}
              value={textField}
              onChange={(e) => setTextField(e.target.value)}
            />
          </Box>

          {currentUserInfo?.color ? null : (
            <>
              <Box mt={2} mb={1}>
                <MainText color="regular">Escolha uma cor para o seu avatar.</MainText>
                <MainText color="regular">Essa cor não poderá ser alterada.</MainText>
              </Box>

              <Box style={{ width: "100%", overflowX: "scroll" }} ref={ScrollBoxRef}>
                <Box display={"flex"} gap={1} alignItems={"center"}>
                  {UtilFunctions.colorList.map((color) => (
                    <Box
                      ref={selectedColor === color ? SelectedColorRef : undefined}
                      key={color}
                      p={1}
                      style={{
                        borderRadius: 15,
                        backgroundColor: selectedColor === color ? GreyPallet.Light : undefined,
                      }}
                    >
                      <Avatar
                        style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: color }}
                        onClick={() => setSelectedColor(color)}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}
          <Box my={3}>
            <MainSolidButton
              loading={loading}
              text="Salvar"
              icon={<SaveRounded />}
              onClick={handleSave}
            />
          </Box>
        </Box>
      </DialogContent>
    </WideDialog>
  );
}
